import { useReducer, useCallback, useRef, useEffect } from 'react';

import {
  AidModelCalculationRequestCalculationGiftTargetType,
  AidModelCalculationRequestGrantCap,
} from 'api/generated/schemas';

import {
  DEFAULT_GIFT_TARGET,
  DEFAULT_GRANT_CAP,
} from '../calculations-step.constants';

import type {
  CalculationGiftTargetType,
  CalculationGrantCap,
  CalculationSettingsDraft,
} from '../calculations-step.types';
import type {
  AidModelCalculationDto,
  AidModelCalculationRequest,
} from 'api/generated/schemas';

interface DraftState {
  settings: CalculationSettingsDraft;
  initialSnapshot: CalculationSettingsDraft;
}

type DraftAction =
  | { type: 'INIT'; payload: AidModelCalculationDto | null | undefined }
  | { type: 'PATCH'; payload: Partial<CalculationSettingsDraft> }
  | { type: 'RESET' };

const createDefaultSettings = (): CalculationSettingsDraft => ({
  calculationGiftTargetType: DEFAULT_GIFT_TARGET,
  includeRequiredFees: false,
  adjustmentLoans: null,
  adjustmentWorkStudy: null,
  calculateNewNeed: false,
  grantCap: DEFAULT_GRANT_CAP,
  grantCapAmount: null,
});

const fromDto = (
  dto: AidModelCalculationDto | null | undefined
): CalculationSettingsDraft => {
  const defaults = createDefaultSettings();

  if (!dto) {
    return defaults;
  }

  return {
    calculationGiftTargetType:
      dto.calculationGiftTargetType ?? defaults.calculationGiftTargetType,
    includeRequiredFees: dto.calculationGiftTargetIncludeRequiredFees === true,
    adjustmentLoans: dto.adjustmentLoans ?? null,
    adjustmentWorkStudy: dto.adjustmentWorkStudy ?? null,
    calculateNewNeed: dto.calculateNewNeed ?? false,
    grantCap: dto.grantCap ?? defaults.grantCap,
    grantCapAmount: dto.grantCapAmount ?? null,
  };
};

const draftReducer = (state: DraftState, action: DraftAction): DraftState => {
  switch (action.type) {
    case 'INIT': {
      const settings = fromDto(action.payload);

      return { settings, initialSnapshot: settings };
    }

    case 'PATCH':
      return {
        ...state,
        settings: { ...state.settings, ...action.payload },
      };

    case 'RESET':
      return { ...state, settings: state.initialSnapshot };

    default:
      return state;
  }
};

const createInitialState = (): DraftState => ({
  settings: createDefaultSettings(),
  initialSnapshot: createDefaultSettings(),
});

export interface UseCalculationDraftResult {
  settings: CalculationSettingsDraft;
  hasChanges: boolean;
  setGiftTarget: (value: CalculationGiftTargetType) => void;
  setIncludeRequiredFees: (value: boolean) => void;
  setAdjustmentLoans: (value: number | null) => void;
  setAdjustmentWorkStudy: (value: number | null) => void;
  setCalculateNewNeed: (value: boolean) => void;
  setGrantCap: (value: CalculationGrantCap) => void;
  setGrantCapAmount: (value: number | null) => void;
  reset: () => void;
  markForReinit: () => void;
  toRequest: () => AidModelCalculationRequest;
}

export const useCalculationDraft = (
  serverData: AidModelCalculationDto | null | undefined
): UseCalculationDraftResult => {
  const [state, dispatch] = useReducer(draftReducer, createInitialState());
  const initialized = useRef(false);

  useEffect(() => {
    if (serverData && !initialized.current) {
      dispatch({ type: 'INIT', payload: serverData });
      initialized.current = true;
    }
  }, [serverData]);

  const setGiftTarget = useCallback(
    (value: CalculationGiftTargetType) =>
      dispatch({
        type: 'PATCH',
        payload: { calculationGiftTargetType: value },
      }),
    []
  );

  const setIncludeRequiredFees = useCallback(
    (value: boolean) =>
      dispatch({ type: 'PATCH', payload: { includeRequiredFees: value } }),
    []
  );

  const setAdjustmentLoans = useCallback(
    (value: number | null) =>
      dispatch({ type: 'PATCH', payload: { adjustmentLoans: value } }),
    []
  );

  const setAdjustmentWorkStudy = useCallback(
    (value: number | null) =>
      dispatch({ type: 'PATCH', payload: { adjustmentWorkStudy: value } }),
    []
  );

  const setCalculateNewNeed = useCallback(
    (value: boolean) =>
      dispatch({ type: 'PATCH', payload: { calculateNewNeed: value } }),
    []
  );

  const setGrantCap = useCallback(
    (value: CalculationGrantCap) =>
      dispatch({ type: 'PATCH', payload: { grantCap: value } }),
    []
  );

  const setGrantCapAmount = useCallback(
    (value: number | null) =>
      dispatch({ type: 'PATCH', payload: { grantCapAmount: value } }),
    []
  );

  const reset = useCallback(() => dispatch({ type: 'RESET' }), []);

  const markForReinit = useCallback(() => {
    initialized.current = false;
  }, []);

  const toRequest = useCallback((): AidModelCalculationRequest => {
    const { settings } = state;
    const isPercentOfTuition =
      settings.calculationGiftTargetType ===
      AidModelCalculationRequestCalculationGiftTargetType.TUITION;
    const isNoGrantCap =
      settings.grantCap === AidModelCalculationRequestGrantCap.NO_GRANT_CAP;

    return {
      calculationGiftTargetType: settings.calculationGiftTargetType,
      calculationGiftTargetIncludeRequiredFees: isPercentOfTuition
        ? settings.includeRequiredFees
        : undefined,
      grantCap: settings.grantCap,
      adjustmentLoans: settings.adjustmentLoans ?? undefined,
      adjustmentWorkStudy: settings.adjustmentWorkStudy ?? undefined,
      calculateNewNeed: settings.calculateNewNeed,
      grantCapAmount: isNoGrantCap
        ? undefined
        : settings.grantCapAmount ?? undefined,
    };
  }, [state]);

  const { settings, initialSnapshot } = state;

  const hasChanges = (
    Object.keys(settings) as (keyof CalculationSettingsDraft)[]
  ).some((key) => settings[key] !== initialSnapshot[key]);

  return {
    settings,
    hasChanges,
    setGiftTarget,
    setIncludeRequiredFees,
    setAdjustmentLoans,
    setAdjustmentWorkStudy,
    setCalculateNewNeed,
    setGrantCap,
    setGrantCapAmount,
    reset,
    markForReinit,
    toRequest,
  };
};
